/**
 * FeedbackSection.jsx
 * components/user/sections/FeedbackSection.jsx
 *
 * LOGIN REQUIRED: Users must be logged in to view and submit feedback
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../../context/UserAuthContext";
import { apiFetch } from "../../helpers";

const EMPTY_FORM = { rating: 5, title: "", message: "", name: "" };

function StarRating({ value, onChange, size = 24, readOnly = false }) {
  const [hov, setHov] = useState(0);
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          onClick={() => !readOnly && onChange && onChange(n)}
          onMouseEnter={() => !readOnly && setHov(n)}
          onMouseLeave={() => !readOnly && setHov(0)}
          style={{
            fontSize: size,
            cursor: readOnly ? "default" : "pointer",
            color: n <= (hov || value) ? "#F59E0B" : "rgba(255,255,255,.2)",
            transition: "color .15s",
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

export default function FeedbackSection() {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useUserAuth();

  const [feedbacks, setFeedbacks] = useState([]); // ✅ Initialize as empty array
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...EMPTY_FORM, name: user?.name || "" });
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSub] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filter, setFilter] = useState(0);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    setLoading(true);

    try {
      const json = await apiFetch(`${process.env.REACT_APP_GET_ALL_FEEDBACKS}`);

      if (json.status) {
        setFeedbacks(json.data?.feedbacks || []);
      } else {
        setFeedbacks([]);
      }
    } catch (e) {
      console.error("Error fetching feedbacks:", e);
      setFeedbacks([]);
    }

    setLoading(false);
  };

  const openAdd = () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    setForm({
      ...EMPTY_FORM,
      name: user?.name || "",
    });

    setEditing(null);
    setError("");
    setShowForm(true);
  };

  const openEdit = (fb) => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    setForm({
      rating: fb.rating,
      title: fb.title || "",
      message: fb.message,
      name: fb.name || "",
    });

    setEditing(fb.feedback_id || fb.id);

    setError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setError("");
  };

  const handleSubmit = async () => {
    if (!form.message.trim()) return setError("Please write your feedback.");
    setSub(true);
    setError("");
    try {
      if (editing) {
        const json = await apiFetch(
          `${process.env.REACT_APP_UPDATE_FEEDBACK}`,
          {
            method: "POST",
            body: {
              feedback_id: editing,
              rating: form.rating,
              title: form.title,
              message: form.message,
              name: form.name,
            },
          },
        );
        if (json.status) {
          await fetchFeedbacks();
          setSuccess("Feedback updated!");
          closeForm();
        } else setError(json.message || "Update failed.");
      } else {
        const json = await apiFetch(`${process.env.REACT_APP_ADD_FEEDBACK}`, {
          method: "POST",
          body: {
            patient_id: user?.user_id || null,
            rating: form.rating,
            title: form.title,
            message: form.message,
            name: form.name || user?.name,
          },
        });
        if (json.status) {
          await fetchFeedbacks();
          setSuccess("Thanks for your feedback! 🎉");
          closeForm();
        } else setError(json.message || "Submission failed.");
      }
    } catch (e) {
      console.error(e);
      setError("Network error. Please try again.");
    }
    setSub(false);
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleDelete = async () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    try {
      const json = await apiFetch(`${process.env.REACT_APP_DELETE_FEEDBACK}`, {
        method: "POST",
        body: {
          feedback_id: deleteId,
          patient_id: user?.patient_id,
        },
      });

      if (json.status) {
        await fetchFeedbacks();
        setDeleteId(null);
      }
    } catch (e) {
      console.error("Delete failed:", e);
    }
  };

  // ✅ Safely filter feedbacks - ensure it's always an array
  const displayed = Array.isArray(feedbacks)
    ? filter > 0
      ? feedbacks.filter((f) => f.rating === filter)
      : feedbacks
    : [];

  // ✅ Safely calculate average rating
  const avgRating =
    Array.isArray(feedbacks) && feedbacks.length > 0
      ? (
          feedbacks.reduce((a, b) => a + (b.rating || 0), 0) / feedbacks.length
        ).toFixed(1)
      : "0.0";

  const S = styles;


  return (
    <section id="feedback" style={S.section}>
      <div style={{
        padding: 33,
      }}></div>
      <div style={S.bgDeco} />
      <div style={S.container}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <span style={S.pill}>💬 Patient Reviews</span>
          <h2 style={S.heading}>
            What Our <span style={S.green}>Patients Say</span>
          </h2>
          <p style={S.sub}>Real experiences from our valued patients</p>
        </div>

        {/* Stats bar */}
        <div style={S.statsBar}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, fontWeight: 900, color: "#F59E0B" }}>
              {avgRating}
            </div>
            <StarRating
              value={Math.round(parseFloat(avgRating))}
              size={18}
              readOnly
            />
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,.45)",
                marginTop: 4,
              }}
            >
              {Array.isArray(feedbacks) ? feedbacks.length : 0} reviews
            </div>
          </div>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {[5, 4, 3, 2, 1].map((star) => {
              const count = Array.isArray(feedbacks)
                ? feedbacks.filter((f) => f.rating === star).length
                : 0;
              const pct =
                Array.isArray(feedbacks) && feedbacks.length > 0
                  ? (count / feedbacks.length) * 100
                  : 0;
              return (
                <div
                  key={star}
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: "rgba(255,255,255,.5)",
                      width: 12,
                    }}
                  >
                    {star}
                  </span>
                  <span style={{ color: "#F59E0B", fontSize: 14 }}>★</span>
                  <div
                    style={{
                      flex: 1,
                      height: 6,
                      background: "rgba(255,255,255,.08)",
                      borderRadius: 3,
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: "#F59E0B",
                        borderRadius: 3,
                        transition: "width .5s",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      color: "rgba(255,255,255,.4)",
                      width: 24,
                    }}
                  >
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
          <button onClick={openAdd} style={S.btnAdd}>
            ✍️ Write Review
          </button>
        </div>

        {/* Filter + success */}
        {success && <div style={S.successBanner}>{success}</div>}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 24,
            flexWrap: "wrap",
          }}
        >
          {[0, 5, 4, 3, 2, 1].map((n) => (
            <button
              key={n}
              onClick={() => setFilter(n)}
              style={{
                ...S.filterBtn,
                ...(filter === n ? S.filterActive : {}),
              }}
            >
              {n === 0 ? "All Reviews" : `${"★".repeat(n)} ${n} Star`}
            </button>
          ))}
        </div>

        {/* Cards */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <div className="spinner-border" style={{ color: "#F59E0B" }} />
          </div>
        ) : displayed.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              color: "rgba(255,255,255,.4)",
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
            <p>No reviews yet. Be the first to share your experience!</p>
          </div>
        ) : (
          <div style={S.grid}>
            {displayed.map((fb) => {
              const isOwn =
                user &&
                (fb.user_id === user.user_id || fb.patient_id === user.user_id);
              return (
                <div key={fb.feedback_id || fb.id} style={S.card}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <StarRating value={fb.rating || 0} size={16} readOnly />
                      {fb.title && (
                        <div
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            fontSize: 14,
                            marginTop: 6,
                          }}
                        >
                          {fb.title}
                        </div>
                      )}
                    </div>
                    {isOwn && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => openEdit(fb)}
                          style={S.iconBtn}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => setDeleteId(fb.feedback_id || fb.id)}
                          style={{ ...S.iconBtn, color: "#E8323C" }}
                          title="Delete"
                        >
                          🗑
                        </button>
                      </div>
                    )}
                  </div>
                  <p style={S.fbMsg}>"{fb.message}"</p>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginTop: 12,
                    }}
                  >
                    <div style={S.avatar}>
                      {(fb.name || "A").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div
                        style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}
                      >
                        {fb.name || "Anonymous"}
                      </div>
                      <div
                        style={{ fontSize: 11, color: "rgba(255,255,255,.35)" }}
                      >
                        {fb.created_at
                          ? new Date(fb.created_at).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              },
                            )
                          : ""}
                      </div>
                    </div>
                    {isOwn && <span style={S.yourBadge}>Your Review</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div onClick={closeForm} style={S.overlay}>
          <div onClick={(e) => e.stopPropagation()} style={S.modal}>
            <button onClick={closeForm} style={S.closeBtn}>
              ✕
            </button>
            <h3 style={{ color: "#fff", fontWeight: 800, marginBottom: 20 }}>
              {editing ? "Edit Your Review" : "Write a Review"}
            </h3>
            <label style={S.label}>Your Rating *</label>
            <div style={{ marginBottom: 16 }}>
              <StarRating
                value={form.rating}
                onChange={(r) => setForm((f) => ({ ...f, rating: r }))}
                size={32}
              />
            </div>
            <label style={S.label}>Your Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              style={S.input}
              placeholder="Enter your name"
            />
            <label style={S.label}>Review Title</label>
            <input
              value={form.title}
              onChange={(e) =>
                setForm((f) => ({ ...f, title: e.target.value }))
              }
              style={S.input}
              placeholder="Summary of your experience"
            />
            <label style={S.label}>Your Feedback *</label>
            <textarea
              value={form.message}
              onChange={(e) =>
                setForm((f) => ({ ...f, message: e.target.value }))
              }
              style={{ ...S.input, height: 100, resize: "none" }}
              placeholder="Share your experience with us..."
            />
            {error && <div style={S.errorBox}>⚠️ {error}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={closeForm} style={S.btnCancel}>
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ ...S.btnAdd, flex: 1, opacity: submitting ? 0.7 : 1 }}
              >
                {submitting
                  ? "Submitting..."
                  : editing
                    ? "Save Changes"
                    : "Submit Review"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div onClick={() => setDeleteId(null)} style={S.overlay}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ ...S.modal, maxWidth: 360, textAlign: "center" }}
          >
            <div style={{ fontSize: 44, marginBottom: 12 }}>🗑️</div>
            <h3 style={{ color: "#fff", marginBottom: 8 }}>Delete Review?</h3>
            <p
              style={{
                color: "rgba(255,255,255,.5)",
                marginBottom: 24,
                fontSize: 13,
              }}
            >
              This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setDeleteId(null)}
                style={{ ...S.btnCancel, flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{
                  flex: 1,
                  padding: 10,
                  background: "#E8323C",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const styles = {
  section: {
    position: "relative",
    padding: "100px 0",
    background: "linear-gradient(180deg,#0D1F3C 0%,#0A1628 100%)",
    overflow: "hidden",
    fontFamily: "'Nunito','Segoe UI',sans-serif",
  },
  bgDeco: {
    position: "absolute",
    top: -200,
    right: -200,
    width: 600,
    height: 600,
    borderRadius: "50%",
    background:
      "radial-gradient(circle,rgba(245,158,11,.05) 0%,transparent 70%)",
    pointerEvents: "none",
  },
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 1.5rem",
    position: "relative",
  },
  pill: {
    display: "inline-block",
    background: "rgba(245,158,11,.15)",
    border: "1px solid rgba(245,158,11,.3)",
    color: "#F59E0B",
    padding: "6px 16px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 16,
  },
  heading: {
    fontSize: "clamp(28px,4vw,42px)",
    fontWeight: 900,
    color: "#fff",
    marginBottom: 8,
    lineHeight: 1.2,
  },
  green: { color: "#F59E0B" },
  sub: { color: "rgba(255,255,255,.45)", fontSize: 14 },
  statsBar: {
    display: "flex",
    alignItems: "center",
    gap: 32,
    background: "rgba(255,255,255,.04)",
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 16,
    padding: "24px 28px",
    marginBottom: 28,
    flexWrap: "wrap",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
    gap: 20,
  },
  card: {
    background: "rgba(255,255,255,.04)",
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 14,
    padding: "20px 18px",
    transition: "transform .2s",
    position: "relative",
  },
  fbMsg: {
    fontSize: 13,
    color: "rgba(255,255,255,.65)",
    lineHeight: 1.7,
    fontStyle: "italic",
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(29,158,117,.2)",
    border: "1px solid rgba(29,158,117,.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    color: "#1D9E75",
    flexShrink: 0,
  },
  yourBadge: {
    marginLeft: "auto",
    fontSize: 10,
    background: "rgba(29,158,117,.15)",
    border: "1px solid rgba(29,158,117,.3)",
    color: "#1D9E75",
    padding: "2px 8px",
    borderRadius: 20,
  },
  filterBtn: {
    padding: "6px 14px",
    borderRadius: 20,
    border: "1px solid rgba(255,255,255,.12)",
    background: "transparent",
    color: "rgba(255,255,255,.55)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  filterActive: {
    background: "rgba(245,158,11,.15)",
    border: "1px solid #F59E0B",
    color: "#F59E0B",
  },
  iconBtn: {
    padding: "5px 9px",
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,.12)",
    background: "transparent",
    color: "rgba(255,255,255,.6)",
    cursor: "pointer",
    fontSize: 13,
  },
  btnAdd: {
    padding: "10px 20px",
    background: "#1D9E75",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnCancel: {
    padding: "10px 20px",
    background: "rgba(255,255,255,.07)",
    color: "rgba(255,255,255,.7)",
    border: "1px solid rgba(255,255,255,.12)",
    borderRadius: 8,
    fontWeight: 600,
    cursor: "pointer",
  },
  label: {
    display: "block",
    fontSize: 12,
    color: "rgba(255,255,255,.5)",
    marginBottom: 6,
    fontWeight: 600,
  },
  input: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,.12)",
    background: "rgba(255,255,255,.06)",
    color: "#fff",
    fontSize: 13,
    outline: "none",
    marginBottom: 14,
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  errorBox: {
    background: "rgba(232,50,60,.1)",
    border: "1px solid rgba(232,50,60,.3)",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 12,
    color: "#ff8a8a",
    marginBottom: 14,
  },
  successBanner: {
    background: "rgba(29,158,117,.12)",
    border: "1px solid rgba(29,158,117,.3)",
    borderRadius: 8,
    padding: "12px 16px",
    color: "#1D9E75",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 20,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.75)",
    zIndex: 1200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },
  modal: {
    background: "#0D1F3C",
    border: "1px solid rgba(255,255,255,.1)",
    borderRadius: 16,
    padding: "32px 28px",
    width: "100%",
    maxWidth: 480,
    maxHeight: "90vh",
    overflowY: "auto",
    position: "relative",
  },
  closeBtn: {
    position: "absolute",
    top: 14,
    right: 14,
    background: "rgba(255,255,255,.1)",
    border: "none",
    borderRadius: 6,
    color: "#fff",
    width: 30,
    height: 30,
    cursor: "pointer",
    fontSize: 14,
  },
};
